<script lang="ts" setup>
import { Product } from '@/api/generated/api.enum';
import ArrowRightIcon from '@/assets/icons/ArrowRightIcon.vue';
import CustomRadioButton from '@/core/elements/CustomRadioButton.vue';
import IconAvatar from '@/core/elements/IconAvatar.vue';
import { EIconAvatarSize } from '@/enums/iconAvatar.enum';
import {
  applyPercentDiscount,
  formatBillingAmount,
  getLowestSeatPriceCents,
  hasMonthlyBilling,
  monthlyPrice,
  roundDiscount,
  type BillingInterval,
  type CheckoutPriceDisplay,
  type ProductWithMeta,
} from '@/utils/billing';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

const props = withDefaults(
  defineProps<{
    product: ProductWithMeta;
    billingInterval?: BillingInterval;
    checkoutPriceDisplay?: CheckoutPriceDisplay;
    isRecommended?: boolean;
    selectable?: boolean;
    isSelected?: boolean;
  }>(),
  {
    billingInterval: 'year',
    checkoutPriceDisplay: undefined,
    isRecommended: false,
    selectable: false,
    isSelected: false,
  },
);

const { selectable, isSelected } = toRefs(props);
const { t } = useI18n();

const interval = computed<BillingInterval>(() => props.billingInterval);

const hasDiscount = computed(() => (props.product.appliedDiscountPercent ?? 0) > 0);

const isMonthly = computed(() => interval.value === 'month' && hasMonthlyBilling(props.product));

const selectableDescriptionPrefixKey = computed(() => {
  if (props.product.product === Product.school) {
    return isMonthly.value
      ? 'billing.products.school.checkoutMonthlyDescriptionPrefix'
      : 'billing.products.school.checkoutYearlyDescriptionPrefix';
  }

  return isMonthly.value
    ? 'billing.products.team.checkoutMonthlyDescriptionPrefix'
    : 'billing.products.team.checkoutYearlyDescriptionPrefix';
});

const selectableDescriptionUnitKey = computed(() =>
  props.product.product === Product.school
    ? 'billing.products.school.checkoutDescriptionUnit'
    : 'billing.products.team.checkoutDescriptionUnit',
);

const selectableUnitPriceLabel = computed(() => {
  const pricing = props.product.pricing;
  const price = isMonthly.value && pricing?.monthly ? pricing.monthly : pricing?.yearly;
  const baseCents = props.product.supportsSeats ? (price?.perSeatCents ?? 0) : (price?.amountCents ?? 0);
  const shouldApplyDiscount = props.product.product === Product.team && hasDiscount.value;
  const finalCents = shouldApplyDiscount
    ? applyPercentDiscount(baseCents, props.product.appliedDiscountPercent ?? 0)
    : baseCents;
  return formatBillingAmount(finalCents, pricing?.currency);
});

const lowestYearlyTierLabel = computed(() => {
  const pricing = props.product.pricing;
  return formatBillingAmount(getLowestSeatPriceCents(pricing?.yearly ?? {}), pricing?.currency);
});

const selectablePricingLine = computed(() => {
  if (props.product.product === Product.school) {
    return isMonthly.value
      ? t('billing.products.school.checkoutMonthlyLine', { price: selectableUnitPriceLabel.value })
      : t('billing.products.school.checkoutYearlyLine', { price: lowestYearlyTierLabel.value });
  }

  return t(isMonthly.value ? 'billing.products.team.checkoutMonthlyLine' : 'billing.products.team.checkoutYearlyLine', {
    price: selectableUnitPriceLabel.value,
  });
});

const selectablePriceLabel = computed(() => props.checkoutPriceDisplay?.priceLabel ?? props.product.priceLabel);

// "ab 2€ …" (school lowest tier) / "72€ …" (team per-seat) in the market currency.
const pricingCalculatorPriceLabel = computed(() => {
  const pricing = props.product.pricing;
  if (props.product.product === Product.school) return lowestYearlyTierLabel.value;
  return formatBillingAmount(pricing?.yearly?.perSeatCents ?? pricing?.yearly?.amountCents ?? 0, pricing?.currency);
});

const selectableOriginalPriceLabel = computed(
  () => props.checkoutPriceDisplay?.originalPriceLabel ?? props.product.originalPriceLabel,
);

const selectablePriceDetailLabel = computed(
  () => props.checkoutPriceDisplay?.detailLabel ?? selectablePricingLine.value,
);

const emit = defineEmits<{
  (e: 'select', product: ProductWithMeta): void;
  (e: 'update:isSelected', value: boolean): void;
}>();

const onSelect = (): void => {
  if (!props.selectable) return;
  if (!props.isSelected) {
    emit('update:isSelected', true);
  }
  emit('select', props.product);
};

const newPriceClass = computed(() => (hasDiscount.value ? 'text-success' : 'text-tint'));

const rootClasses = computed(() => {
  if (selectable.value) {
    return [
      'relative flex h-fit min-w-0 items-start gap-lg rounded-group p-lg max-md:p-md',
      'transition duration-300 cursor-pointer focus-visible:outline-focus focus-visible:outline-offset-2',
      'hover:bg-tint/10',
      isSelected.value ? 'bg-tint/10' : 'bg-surface-secondary ',
    ].join(' ');
  }
  return 'relative bg-surface-secondary p-lg flex min-w-0 gap-lg rounded-group items-start justify-between max-md:p-md max-md:gap-md';
});
</script>

<template>
  <!-- Selectable mode -->
  <div
    v-if="selectable"
    :class="rootClasses"
    :aria-checked="isSelected"
    role="radio"
    tabindex="0"
    @click="onSelect"
    @keypress.enter.stop="onSelect"
    @keypress.space.stop="onSelect"
  >
    <component
      :is="product.meta.icon"
      :class="['size-icon-xl shrink-0', product.product !== Product.school && 'text-tint']"
    />

    <div class="flex min-w-0 flex-1 flex-col gap-md self-stretch">
      <div class="flex min-w-0 gap-sm flex-col">
        <span class="font-semibold text-heading leading-[170%] break-words">
          {{ $t(product.meta.titleKey) }}
        </span>
        <p class="break-words">
          {{ $t(selectableDescriptionPrefixKey) }}
          <span class="block">
            {{ $t('billing.products.checkoutDescriptionBillingPrefix') }}
            <span class="font-semibold">{{ $t(selectableDescriptionUnitKey) }}</span
            >.
          </span>
        </p>
      </div>

      <div class="flex min-w-0 flex-col">
        <p class="flex min-w-0 flex-wrap items-baseline gap-x-sm break-words text-title font-medium leading-[165%]">
          <span :class="newPriceClass">{{ selectablePriceLabel }}</span>
          <span
            v-if="selectableOriginalPriceLabel"
            class="text-secondary underline underline-offset-[-0.38em] [text-decoration-skip-ink:none]"
          >
            {{ selectableOriginalPriceLabel }}
          </span>
        </p>
        <p class="break-words leading-[170%]">
          {{ $t('billing.products.units.perMonth') }}
          <span class="block text-secondary">
            {{ selectablePriceDetailLabel }}
          </span>
        </p>
      </div>
    </div>

    <div class="flex shrink-0 items-center py-xs">
      <CustomRadioButton name="billing-plan" :model-value="isSelected" @update:model-value="onSelect" />
    </div>
  </div>

  <!-- Default mode -->
  <div v-else :class="rootClasses">
    <IconAvatar :icon="product.meta.icon" :size="EIconAvatarSize.LG" />

    <div class="flex min-w-0 flex-1 flex-col gap-md">
      <div>
        <div class="flex min-w-0 flex-wrap items-center gap-x-sm gap-y-xs">
          <span class="font-semibold text-heading leading-[170%] hyphens-auto break-words">
            Digidoo {{ $t(product.meta.titleKey) }}
          </span>
          <span v-if="isRecommended && !hasDiscount" class="text-warning mt-0.5">
            ({{ $t('general.recommended') }})
          </span>

          <span v-if="hasDiscount" class="text-success font-semibold">
            {{ $t('billing.products.trialDiscount.badge', { percent: roundDiscount(product.appliedDiscountPercent) }) }}
          </span>
        </div>
        <p class="hyphens-auto break-words">{{ $t(product.meta.descriptionKey) }}</p>
        <p class="text-footer mt-xs hyphens-auto break-words">
          {{ $t(product.meta.pricingCalculatorDescriptionKey, { price: pricingCalculatorPriceLabel }) }}
          <span v-if="product.product !== Product.school" class="text-secondary"
            >{{ monthlyPrice(product, product.appliedDiscountPercent ?? 0) }}
            {{ $t('billing.products.units.perMonth') }}</span
          >
        </p>
      </div>
      <p class="text-tint text-title font-medium mt-xs min-w-0 break-words">
        <span
          v-if="product.originalPriceLabel"
          class="underline underline-offset-[-0.38em] [text-decoration-skip-ink:none] text-secondary mr-2"
        >
          {{ product.originalPriceLabel }}
        </span>
        <span :class="newPriceClass">{{ product.priceLabel }}</span>
      </p>
    </div>

    <ArrowRightIcon class="size-icon text-tint" />
  </div>
</template>
